import { ChevronDownIcon, ChevronLeftIcon } from "@heroicons/react/outline";
import { XIcon } from "@heroicons/react/solid";
import classNames from "classnames";
import { useMemo, useState } from "react";
import ReactTooltip from "react-tooltip";

import {
  Accession,
  ApiService,
  Icd10Code,
  Specimen,
  SpecimenProtocolOverride,
} from "pwapi";

import { AccessionSpecimen } from "custom-models";

import {
  getProtocolStatuses,
  procedureEnumToLabel,
  protocolLabels,
} from "shared/utils/specimenUtils";

import {
  noClinicalHistoryString,
  noClinicalHistoryClinicString,
} from "app/constants";
import { useAppSelector } from "app/hooks";
import { useSelectedLab } from "hooks/useSelectedLabStation";
import useSlowMohs from "hooks/useSlowMohs";
import SelectSearchInput from "ui/CaseFormInputs/SelectSearchInput";
import {
  Icd10Option,
  mapIcd10ToOptions,
} from "ui/CaseFormInputs/SelectSearchOptions";
import SpecimenProtocolTags from "ui/components/SpecimenProtocolTags";

const SidebarSpecimensForm = ({
  specimen,
  onSubmit,
  onClose,
  isCaseAccessioned,
  specimens,
  caseType,
}: {
  specimen?: AccessionSpecimen;
  onSubmit: Function;
  onClose: Function;
  isCaseAccessioned?: boolean;
  specimens: AccessionSpecimen[];
  caseType?: Accession.case_type;
}) => {
  const [specimenValues, setSpecimensValues] = useState<AccessionSpecimen>({
    procedure: specimen?.procedure,
    name: specimen?.name || "",
    type: specimen?.type || Specimen.type.SKIN,
    site: specimen?.site,
    clinical_history: specimen?.clinical_history || "",
    materials_received: specimen?.materials_received || null,
    id: specimen?.id,
    protocol_overrides: specimen?.protocol_overrides || [],
    icd10codes: specimen?.icd10codes || [],
    lis_state: specimen?.lis_state,
    slow_mohs_source_id: specimen?.slow_mohs_source_id || undefined,
    slow_mohs_source_name:
      specimen?.slow_mohs_source_name || specimen?.name || "",
  });

  const protocolOverrides = specimenValues.protocol_overrides || [];

  const handleOnChange = (
    field: string,
    value: string | number | undefined
  ) => {
    setSpecimensValues({
      ...specimenValues,
      [field]: value,
    });
  };

  const onClickAddIcd10Code = () => {
    setSpecimensValues({
      ...specimenValues,
      icd10codes: [...(specimenValues.icd10codes || []), { code: "" }],
    });
  };

  const onClickRemoveIcd10Code = (idx: number) => {
    if (idx > (specimenValues.icd10codes?.length || 0)) return;
    setSpecimensValues({
      ...specimenValues,
      icd10codes: [
        ...(specimenValues.icd10codes || []).slice(0, idx),
        ...(specimenValues.icd10codes || []).slice(idx + 1),
      ],
    });
  };

  const onChangeIcd10Code = (newValue: Icd10Code, idx: number) => {
    const updatedValues = [...(specimenValues.icd10codes || [])];
    updatedValues[idx] = newValue;

    setSpecimensValues({
      ...specimenValues,
      icd10codes: updatedValues,
    });
  };

  const setProtocolOverride = (
    p: SpecimenProtocolOverride.protocol,
    v: string
  ) => {
    const newProtocolOverrides = protocolOverrides.filter(
      (po) => po.protocol !== p
    );

    if (v !== "null") {
      newProtocolOverrides.push({ protocol: p, active: v === "true" });
    }

    setSpecimensValues({
      ...specimenValues,
      protocol_overrides: newProtocolOverrides,
    });
  };

  const difEnabled = useAppSelector(
    (state) => !!state.sys.features.switches?.difProtocol
  );

  const selectedLab = useSelectedLab();
  const selectedLabTranscriptionEntry =
    selectedLab?.settings?.transcription_entry?.active;

  const pashReflexEnabled = selectedLab?.settings?.pash_reflex?.active;
  const pasfReflexEnabled = selectedLab?.settings?.pasf_reflex?.active;

  const slowMohsEnabled = useAppSelector(
    (state) => !!state.sys.features.switches?.slowMohsProtocol
  );
  const protocolStatuses = getProtocolStatuses(
    specimenValues,
    true,
    selectedLab.settings
  );
  const protocols = Object.entries(
    getProtocolStatuses(specimenValues, undefined, selectedLab.settings)
  )
    .filter(
      ([protocol, status]) =>
        status.active &&
        (protocol !== "dif" || difEnabled) &&
        (protocol !== SpecimenProtocolOverride.protocol.PASH_REFLEX ||
          pashReflexEnabled) &&
        (protocol !== SpecimenProtocolOverride.protocol.PASF_REFLEX ||
          pasfReflexEnabled) &&
        (protocol !== SpecimenProtocolOverride.protocol.SLOW_MOHS ||
          slowMohsEnabled)
    )
    .map(([protocol, _]) => protocol) as SpecimenProtocolOverride.protocol[];

  const protocolsStr = protocols.join(",");
  const initialProtocolsStr = useMemo(() => protocolsStr, []);

  const [showProtocols, setShowProtocols] = useState(false);

  const pasfLabStain = useAppSelector((state) =>
    Object.values(state.labStains).find(
      (ls) =>
        ls.lab_id === selectedLab.id &&
        state.stains[ls.stain_id]?.abbreviation === "PAS-F"
    )
  );

  const pashLabStain = useAppSelector((state) =>
    Object.values(state.labStains).find(
      (ls) =>
        ls.lab_id === selectedLab.id &&
        state.stains[ls.stain_id]?.abbreviation === "PAS-H"
    )
  );

  const difLabStain = useAppSelector((state) =>
    Object.values(state.labStains).find(
      (ls) =>
        ls.lab_id === selectedLab.id &&
        state.stains[ls.stain_id]?.name === "DIF"
    )
  );

  const alopeciaPanel = useAppSelector((state) =>
    Object.values(state.labPanels).find(
      (lp) =>
        lp.lab_id === selectedLab.id && lp.name.toLowerCase() === "alopecia"
    )
  );

  const protocolsToShow = ["alopecia", "nail" /* "whole_specimen",  */];

  if (difEnabled) protocolsToShow.push("dif");
  if (slowMohsEnabled)
    protocolsToShow.push(SpecimenProtocolOverride.protocol.SLOW_MOHS);

  if (pashReflexEnabled)
    protocolsToShow.push(SpecimenProtocolOverride.protocol.PASH_REFLEX);
  if (pasfReflexEnabled)
    protocolsToShow.push(SpecimenProtocolOverride.protocol.PASF_REFLEX);

  const isNoClinicalHistoryChecked =
    specimenValues.clinical_history === noClinicalHistoryString;

  const {
    slowMohsSegmentCount,
    previousSpecimenIsSlowMohs,
    partOfLastSlowMohsGroup,
    isLastSpecimen,
    protocolTagsSpecimens,
    onChangeSlowMohsAddToGroup,
    onFocusSlowMohsCreateGroup,
    onChangeSlowMohsCreateGroup,
  } = useSlowMohs(
    specimenValues,
    specimen,
    specimens,
    isCaseAccessioned,
    setSpecimensValues
  );

  return (
    <form
      className="SpecimenForm"
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        const icd10code_ids = (specimenValues.icd10codes || [])
          .filter((code) => code.id)
          .map((code) => code.id || 0);
        onSubmit(
          {
            ...specimenValues,
            icd10code_ids,
          },
          slowMohsSegmentCount
        );
      }}
    >
      <input
        type="text"
        className="SpecimenForm__nameInput"
        value={specimenValues.name || ""}
        onChange={(e) => {
          handleOnChange("name", e.currentTarget.value.toUpperCase());
        }}
        placeholder="Name"
        readOnly={!isCaseAccessioned}
        data-tip={
          !isCaseAccessioned
            ? "Cannot change Specimen names at Accessioning"
            : undefined
        }
        required
      />
      <label className="SpecimenFormInput">
        <span className="SpecimenForm__sectionLabel">Type *</span>
        <select
          value={specimenValues.type || Specimen.type.SKIN}
          onChange={(e) => {
            handleOnChange("type", e.currentTarget.value);
          }}
          defaultValue={Specimen.type.SKIN}
          required
        >
          {Object.values(Specimen.type).map((type) => {
            return (
              <option value={type} key={type}>
                {type}
              </option>
            );
          })}
        </select>
      </label>
      <label className="SpecimenFormInput">
        <span className="SpecimenForm__sectionLabel">Site *</span>
        <input
          type="text"
          value={specimenValues.site || ""}
          onChange={(e) => {
            handleOnChange("site", e.currentTarget.value);
          }}
          required
        />
      </label>
      <label className="SpecimenFormInput" htmlFor="specimen__procedure">
        <span className="SpecimenForm__sectionLabel">Procedure *</span>
        <select
          value={specimenValues.procedure || ""}
          onChange={(e) => {
            handleOnChange("procedure", e.currentTarget.value);
          }}
          id="specimen__procedure"
          required
        >
          <option style={{ display: "none" }} value="">
            ...
          </option>
          {Object.values(Specimen.procedure)
            .sort((a, b) => {
              if (a === "o") return 1;
              if (b === "o") return -1;

              const labelA = procedureEnumToLabel(a);
              const labelB = procedureEnumToLabel(b);

              return (labelA || "").localeCompare(labelB || "");
            })
            .filter(
              (v) =>
                specimenValues.procedure === Specimen.procedure.U ||
                v !== Specimen.procedure.U
            )
            .map((procedure) => {
              return (
                <option
                  value={procedure}
                  key={procedure}
                  disabled={procedure === Specimen.procedure.U}
                >
                  {procedureEnumToLabel(procedure)}
                </option>
              );
            })}
        </select>
      </label>
      <div className="SpecimenFormInput SpecimenForm__clinicalHistory">
        <label
          className="SpecimenForm__sectionLabel"
          htmlFor="clinicalHistoryText"
        >
          Clinical History
          {!selectedLabTranscriptionEntry && " *"}
        </label>
        <textarea
          id="clinicalHistoryText"
          value={specimenValues.clinical_history || ""}
          onChange={(e) => {
            e.currentTarget.setCustomValidity("");

            handleOnChange("clinical_history", e.currentTarget.value);

            if (
              !isNoClinicalHistoryChecked &&
              !e.currentTarget.value.trim() &&
              !selectedLabTranscriptionEntry
            ) {
              e.currentTarget.setCustomValidity(
                "Clinical history is required."
              );
            }
          }}
          disabled={isNoClinicalHistoryChecked}
          rows={5}
          required={!selectedLabTranscriptionEntry}
        />
        <div className="SpecimenForm__checkBoxWrapper">
          <input
            id="noClinicalHistory"
            className="SpecimenForm__checkBox"
            type="checkbox"
            checked={isNoClinicalHistoryChecked}
            onChange={(e) => {
              handleOnChange(
                "clinical_history",
                e.currentTarget.checked ? noClinicalHistoryString : ""
              );
            }}
          />
          <label
            className="SpecimenForm__checkBoxLabel"
            htmlFor="noClinicalHistory"
          >
            {noClinicalHistoryClinicString}
          </label>
        </div>
      </div>
      {selectedLab?.settings?.materials_received?.active &&
        ((caseType &&
          [Accession.case_type.CONSULT, Accession.case_type.TC_PC].includes(
            caseType
          )) ||
          specimenValues.materials_received != null) && (
          <div className="SpecimenFormInput SpecimenForm__materialsReceived">
            <label
              className="SpecimenForm__sectionLabel"
              htmlFor="materialsReceivedText"
            >
              Materials Received
            </label>
            <textarea
              id="materialsReceivedText"
              value={specimenValues.materials_received || ""}
              onChange={(e) => {
                handleOnChange("materials_received", e.currentTarget.value);
              }}
              rows={5}
            />
          </div>
        )}
      <div className="SpecimenFormICD10Codes">
        <span className="SpecimenForm__sectionLabel">Clinical ICD-10 Code</span>
        {!!specimenValues.icd10codes?.length &&
          specimenValues.icd10codes.map((code, idx) => {
            return (
              <div className="SpecimenFormInput" key={`${code.code}${idx}`}>
                <SelectSearchInput
                  id={`icd10_code${idx}`}
                  component={Icd10Option}
                  mapToOptions={mapIcd10ToOptions}
                  apiService={(
                    args: Parameters<typeof ApiService.listIcd10Codes>[0]
                  ) => ApiService.listIcd10Codes(args)}
                  filter="code"
                  onChange={(newValue: {
                    label: string;
                    object: Icd10Code;
                    value: number;
                  }) => onChangeIcd10Code(newValue.object, idx)}
                >
                  {code.code ? code.code : "Select an ICD-10 code..."}
                </SelectSearchInput>
                <button
                  type="button"
                  onClick={() => {
                    onClickRemoveIcd10Code(idx);
                  }}
                  className="Button Button-purple"
                >
                  <XIcon />
                </button>
              </div>
            );
          })}
        <button
          type="button"
          onClick={onClickAddIcd10Code}
          className="Button Button--purple"
        >
          + Add ICD-10 Code
        </button>
      </div>
      {slowMohsEnabled &&
        protocols.includes(SpecimenProtocolOverride.protocol.SLOW_MOHS) && (
          <>
            <span>Slow Mohs Segments</span>
            <span>
              <em>
                <small>(Includes debulk and margins)</small>
              </em>
            </span>

            <label
              className="SpecimenFormInput SlowMohsFormInput"
              data-tip={
                !previousSpecimenIsSlowMohs
                  ? "Previous Specimen does not have Slow Mohs Protocol"
                  : !isLastSpecimen
                  ? "Slow Mohs Segments group can only be changed for the last Specimen in a Case"
                  : undefined
              }
            >
              <span className="SpecimenForm__sectionLabel">
                Add to previous group
              </span>
              <input
                type="checkbox"
                checked={partOfLastSlowMohsGroup}
                disabled={!isLastSpecimen || !previousSpecimenIsSlowMohs}
                onChange={onChangeSlowMohsAddToGroup}
              />
            </label>

            <em className="SlowMohsOr">
              <small>or</small>
            </em>

            <label
              className="SpecimenFormInput SlowMohsFormInput"
              data-tip={
                isCaseAccessioned
                  ? '"Create Group With Copies" is only available at accessioning'
                  : !isLastSpecimen
                  ? "Slow Mohs Segments group can only be changed for the last Specimen in a Case"
                  : undefined
              }
            >
              <span className="SpecimenForm__sectionLabel">
                Create group with copies
              </span>
              <input
                type="number"
                value={slowMohsSegmentCount || ""}
                min={2}
                max={9}
                disabled={!isLastSpecimen || isCaseAccessioned}
                onFocus={onFocusSlowMohsCreateGroup}
                onChange={onChangeSlowMohsCreateGroup}
              />
            </label>
            <ReactTooltip />
          </>
        )}
      <div className="SpecimenForm__protocols">
        <div className="SpecimenForm__protocols__header">
          <h4>
            {!showProtocols && protocols.length === 0
              ? "No Special Protocols"
              : "Protocols:"}
          </h4>
          <button
            type="button"
            onClick={() => setShowProtocols(!showProtocols)}
          >
            {showProtocols ? "Hide Overrides " : "Show Overrides "}
            {showProtocols ? (
              <ChevronDownIcon height={10} />
            ) : (
              <ChevronLeftIcon height={10} />
            )}
          </button>
        </div>
        {showProtocols && <ReactTooltip />}
        {showProtocols ? (
          protocolsToShow.map((p) => (
            <div key={p} style={{ marginBottom: "1em" }}>
              <div style={{ display: "flex" }}>
                <div
                  className={classNames("Tag", {
                    "Tag--blue":
                      protocolOverrides.find((po) => po.protocol === p) ===
                        undefined &&
                      protocolStatuses[p as SpecimenProtocolOverride.protocol]
                        .active,
                    "Tag--gray":
                      protocolOverrides.find((po) => po.protocol === p) ===
                        undefined &&
                      !protocolStatuses[p as SpecimenProtocolOverride.protocol]
                        .active,
                    "Tag--blue--bordered":
                      protocolOverrides.find((po) => po.protocol === p)
                        ?.active === true,
                    "Tag--gray--bordered":
                      protocolOverrides.find((po) => po.protocol === p)
                        ?.active === false,
                  })}
                  data-tip={
                    protocolStatuses[p as SpecimenProtocolOverride.protocol]
                      .reason
                  }
                >
                  {protocolLabels[p as SpecimenProtocolOverride.protocol]}
                </div>
                <select
                  style={{ marginLeft: "auto", width: "auto" }}
                  onChange={(ev) =>
                    setProtocolOverride(
                      p as SpecimenProtocolOverride.protocol,
                      ev.currentTarget.value
                    )
                  }
                  value={
                    protocolOverrides.find((po) => po.protocol === p) ===
                    undefined
                      ? "null"
                      : protocolOverrides.find((po) => po.protocol === p)
                          ?.active
                      ? "true"
                      : "false"
                  }
                >
                  <option value="null">
                    Auto:{" "}
                    {protocolStatuses[p as SpecimenProtocolOverride.protocol]
                      .active
                      ? "ON"
                      : "OFF"}
                  </option>
                  <option value="true">Override: ON</option>
                  <option value="false">Override: OFF</option>
                </select>
              </div>
            </div>
          ))
        ) : (
          <SpecimenProtocolTags
            specimen={{ ...specimenValues, protocols }}
            specimens={protocolTagsSpecimens}
          />
        )}
        {isCaseAccessioned && protocolsStr !== initialProtocolsStr && (
          <div
            style={{ fontStyle: "italic", fontSize: ".8em", color: "darkred" }}
          >
            Changing Protocols after Accessioning is not fully supported.
          </div>
        )}

        {protocols.includes(SpecimenProtocolOverride.protocol.NAIL) &&
          !pasfLabStain &&
          !pashReflexEnabled && (
            <p className="red">
              <em>
                <small>
                  NAIL Protocol should order a PAS-F Stain by default, but none
                  could be found for {selectedLab.name}
                </small>
              </em>
            </p>
          )}

        {protocols.includes(SpecimenProtocolOverride.protocol.NAIL) &&
          !pashLabStain &&
          pashReflexEnabled && (
            <p className="red">
              <em>
                <small>
                  NAIL Protocol should order a PAS-H Stain by default, but none
                  could be found for {selectedLab.name}
                </small>
              </em>
            </p>
          )}

        {protocols.includes(SpecimenProtocolOverride.protocol.PASH_REFLEX) &&
          !pashLabStain && (
            <p className="red">
              <em>
                <small>
                  {protocolLabels.pash_reflex} Protocol should order a PAS-H
                  Stain by default, but none could be found for{" "}
                  {selectedLab.name}
                </small>
              </em>
            </p>
          )}

        {protocols.includes(SpecimenProtocolOverride.protocol.PASF_REFLEX) &&
          !pasfLabStain && (
            <p className="red">
              <em>
                <small>
                  {protocolLabels.pasf_reflex} Protocol should order a PAS-F
                  Stain by default, but none could be found for{" "}
                  {selectedLab.name}
                </small>
              </em>
            </p>
          )}

        {protocols.includes(SpecimenProtocolOverride.protocol.ALOPECIA) &&
          !alopeciaPanel && (
            <p className="red">
              <em>
                <small>
                  ALOPECIA Protocol should order an Alopecia Panel by default,
                  but none could be found for {selectedLab.name}
                </small>
              </em>
            </p>
          )}

        {difEnabled &&
          protocols.includes(SpecimenProtocolOverride.protocol.DIF) &&
          !difLabStain && (
            <p className="red">
              <em>
                <small>
                  DIF Protocol is not configured for {selectedLab.name}
                </small>
              </em>
            </p>
          )}
      </div>
      <button
        onClick={() => {
          onClose();
        }}
        type="button"
        className="Button Button--purple SpecimenForm__close"
      >
        Cancel
      </button>
      <button type="submit" className="Button Button--purple">
        Save Specimen
      </button>
    </form>
  );
};

export default SidebarSpecimensForm;
